import React from 'react';
import { useApolloClient } from '@apollo/client';
import { toast } from 'sonner';
import {
  getCachePersistor,
  reassignTrackedQueriesOwner,
} from '@bilderit/offline-link';
import { useMergeIdentityClaimMutation } from '@/generated/codegen';
import { LoadingMessageCard } from '@/components/LoadingMessageCard';
import { filterObjByArray } from '@/utils/filterInput';
import {
  abandonMerge,
  completeMerge,
  readReadyMerge,
  reloadApp,
} from '@/utils/identityClaimSession';
import { MERGE_IDENTITY_CLAIM_INPUT } from './IdentityClaimSchema';

const GENERIC_ERROR_MESSAGE = 'No pudimos unir tus usuarios. Intentá de nuevo.';

/**
 * Runs the merge once the page is back as the managed user, with the other
 * user's session copied aside; then leaves the app signed in as the other user.
 */
export const IdentityClaimMergeResumer: React.FC = () => {
  const client = useApolloClient();
  const [mergeIdentityClaim] = useMergeIdentityClaimMutation();
  const [merging, setMerging] = React.useState(false);
  const started = React.useRef(false);

  React.useEffect(() => {
    if (started.current) return;
    const merge = readReadyMerge();
    if (!merge) return;
    started.current = true;
    setMerging(true);

    const run = async () => {
      try {
        await mergeIdentityClaim({
          variables: {
            input: filterObjByArray(MERGE_IDENTITY_CLAIM_INPUT, {
              other_access_token: merge.otherAccessToken,
            }),
          },
        });
      } catch (error) {
        abandonMerge();
        setMerging(false);
        toast.error(
          error instanceof Error && error.message
            ? error.message
            : GENERIC_ERROR_MESSAGE
        );
        return;
      }
      completeMerge();
      // The managed identity no longer exists: what it left queued offline
      // goes on under the other one, which is now the same user.
      await reassignTrackedQueriesOwner(
        merge.managedUsername,
        merge.otherUsername
      ).catch((error) => console.error(error));
      await Promise.all(
        [merge.managedUsername, merge.otherUsername].map((identity) =>
          getCachePersistor(client, identity)
            .purge()
            .catch(() => undefined)
        )
      );
      reloadApp();
    };
    run();
  }, [client, mergeIdentityClaim]);

  if (!merging) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-6">
      <LoadingMessageCard message="Uniendo tus usuarios..." />
    </div>
  );
};
